import { useRef, useState } from 'react'
import { ScriptCardDeck } from '../components/ScriptCardDeck'
import { BottomActionBar } from '../components/BottomActionBar'
import { ArrangementList } from '../components/ArrangementList'
import { pwaApi } from '../api'
import type { ConversationState, ConversationActions } from '../hooks/useConversation'
import type { Arrangement } from '../types/arrangement'
import { collapseRepeatedPhrases, mergeFinalSegments } from '../utils/textSanitize'

interface Screen2Positions {
  titleTop: number
  convTop: number
  actionBarBottom: number
  convHeightPct: number
}

interface Props {
  positions: Screen2Positions
  viewportWidth: number
  fontSize: number
  onFontSizeChange: (action: 'increase' | 'decrease') => void
  conv: ConversationState & ConversationActions
  onConfirm: () => void
  onGoToE3: () => void
  addE3Arrangement: (a: Arrangement) => void
  arrangementList: Arrangement[]  // Globalny stan z e3Data
}

export function Screen2({ positions, fontSize, conv, onConfirm, onGoToE3, addE3Arrangement, arrangementList }: Props) {
  const deckNextRef = useRef<(() => void) | null>(null)
  const [showPanel, setShowPanel]     = useState(false)
  const [isRecording, setIsRecording] = useState(false)
  const [currentText, setCurrentText] = useState('')
  const panelHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // localList USUNIĘTE - używamy tylko globalnego arrangementList

  const speechRef = useRef<any>(null)
  const currentTextRef = useRef('')
  const isProcessingRef = useRef(false)

  const isRealConvId = (id: string | number | null | undefined): id is number => {
    if (id === null || id === undefined) return false
    if (typeof id === 'string' && id.startsWith('tmp_')) return false
    const n = Number(id)
    return Number.isInteger(n) && n > 0
  }

  const handleConfirmButton = () => {
    if (isProcessingRef.current) return
    isProcessingRef.current = true
    setTimeout(() => { isProcessingRef.current = false }, 300)
    console.log('[Screen2] handleConfirmButton - START')

    // Najpierw zmień kartę / przejdź do E3
    if (deckNextRef.current) {
      deckNextRef.current()
    } else {
      onGoToE3()
    }

    const pendingText = currentText.trim() || currentTextRef.current.trim()
    
    // Jeśli jest tekst w trakcie dyktowania - dodaj jako ustalenie i wyślij do bazy
    if (pendingText) {
      const newItem: Arrangement = { id: crypto.randomUUID(), text: pendingText, done: false }
      addE3Arrangement(newItem)
      setCurrentText('')
      currentTextRef.current = ''
      
      // LAZY LOADING: natychmiast wyślij do bazy
      if (isRealConvId(conv.conversationId)) {
        const updatedList = [...arrangementList, newItem]
        console.log('[Screen2] handleConfirmButton - PATCH to backend:', updatedList.length, 'items')
        pwaApi.patchArrangements(conv.conversationId, updatedList).catch(err => 
          console.error('[Screen2] PATCH failed:', err)
        )
      }
    } else if (arrangementList.length > 0 && isRealConvId(conv.conversationId)) {
      console.log('[Screen2] handleConfirmButton - syncing existing arrangements to backend')
      pwaApi.patchArrangements(conv.conversationId, arrangementList).catch(err => 
        console.error('[Screen2] PATCH failed:', err)
      )
    }
  }

  const handleMicHoldStart = () => {
    console.log('[Screen2] handleMicHoldStart - START')
    setIsRecording(true)
    setCurrentText('')
    currentTextRef.current = ''
    if (panelHideTimer.current) { clearTimeout(panelHideTimer.current); panelHideTimer.current = null }
    setShowPanel(true)

    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    console.log('[Screen2] handleMicHoldStart - SR available:', !!SR)
    if (!SR) {
      console.error('[Screen2] handleMicHoldStart - SpeechRecognition NOT available!')
      return
    }
    if (speechRef.current) { 
      console.log('[Screen2] handleMicHoldStart - stopping previous SR')
      try { speechRef.current.stop() } catch(_){} 
    }
    const sr = new SR()
    sr.lang = 'pl-PL'; sr.continuous = true; sr.interimResults = false
    console.log('[Screen2] handleMicHoldStart - SR created, lang:', sr.lang)
    
    sr.onresult = (e: any) => {
      const results = Array.from(e.results as any[])
      const text = mergeFinalSegments(results.map((r: any) => r[0].transcript))
      const isFinal = results.every((r: any) => r.isFinal)
      console.log('[Screen2] SR onresult - text:', text.slice(0, 50), 'isFinal:', isFinal, 'results:', results.length)
      // e.results zawiera SKUMULOWANĄ transkrypcję całej sesji — trzeba PRZYPISAĆ,
      // nie doklejać (doklejanie duplikowałoby treść przy każdym zdarzeniu onresult).
      currentTextRef.current = text
      console.log('[Screen2] SR onresult - currentTextRef updated:', currentTextRef.current.slice(0, 50))
      // Sync do state dla UI
      setCurrentText(currentTextRef.current)
    }
    sr.onerror = (e: any) => { 
      console.error('[Screen2] SR onerror:', e.error, e.message)
    }
    sr.onend = () => { 
      console.log('[Screen2] SR onend')
    }
    
    try {
      sr.start()
      console.log('[Screen2] handleMicHoldStart - SR started successfully')
    } catch (err) {
      console.error('[Screen2] handleMicHoldStart - SR start failed:', err)
    }
    speechRef.current = sr
  }

  const handleMicHoldEnd = () => {
    console.log('[Screen2] handleMicHoldEnd - START, currentTextRef:', currentTextRef.current.slice(0, 50))
    setIsRecording(false)
    // Nie zatrzymuj SR natychmiast - daj czas na finalne wyniki
    const sr = speechRef.current
    if (sr) { 
      setTimeout(() => {
        try { sr.stop() } catch(_){}
        speechRef.current = null
        
        const text = collapseRepeatedPhrases(currentTextRef.current.trim())
        console.log('[Screen2] handleMicHoldEnd DELAYED - final text:', text.slice(0, 50))
        if (text) {
          const newItem: Arrangement = { id: crypto.randomUUID(), text, done: false }
          console.log('[Screen2] handleMicHoldEnd - creating newItem:', newItem)
          addE3Arrangement(newItem)
          console.log('[Screen2] handleMicHoldEnd - called addE3Arrangement')
          currentTextRef.current = ''
          setCurrentText('')
        } else {
          console.log('[Screen2] handleMicHoldEnd - no text, keeping ref for next attempt')
        }
        // Panel zostaje widoczny; chowaj dopiero po 3s
        panelHideTimer.current = setTimeout(() => setShowPanel(false), 3000)
      }, 400)  // 400ms delay na finalne wyniki
    } else {
      // Fallback - SR nie był aktywny
      const text = currentTextRef.current.trim()
      if (text) {
        const newItem: Arrangement = { id: crypto.randomUUID(), text, done: false }
        addE3Arrangement(newItem)
        currentTextRef.current = ''
        setCurrentText('')
      }
      panelHideTimer.current = setTimeout(() => setShowPanel(false), 3000)
    }
  }

  return (
    <div className="relative w-full h-full bg-[#0a0a0f]">

      <div style={{
        position: 'absolute', top: `${positions.titleTop}%`, left: 0, right: 0,
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        color: '#D9D9D9', textAlign: 'center',
        fontFamily: '"Inter", sans-serif', fontSize: 'clamp(24px, 4vw, 32px)',
        fontWeight: 200, letterSpacing: '3px',
        fontFeatureSettings: "'liga' off, 'clig' off", whiteSpace: 'nowrap',
      }}>Twój Skrypt</div>

      <div style={{
        position: 'absolute', top: `${positions.convTop}%`,
        left: '4.8%', right: '4.8%', height: `${positions.convHeightPct}%`,
      }}>
        <ScriptCardDeck
          briefing={conv.scriptBriefing}
          complianceWarning={conv.complianceWarning ?? undefined}
          steps={conv.scriptSteps}
          streamDone={conv.scriptStreamDone}
          streamLoading={conv.scriptLoading}
          model={conv.scriptModel || undefined}
          fontSize={fontSize}
          onConfirmLast={onConfirm}
          onNextRef={deckNextRef}
          guardStatus={conv.guardStatus}
        />
      </div>

      <div style={{
        position: 'absolute', bottom: `${positions.actionBarBottom}%`,
        left: 0, right: 0, display: 'flex', justifyContent: 'center',
        transformOrigin: 'center bottom',
      }}>
        <BottomActionBar
          onConfirmTap={handleConfirmButton}
          onMicHoldStart={handleMicHoldStart}
          onMicHoldEnd={handleMicHoldEnd}
          showArrangementsPanel={showPanel}
          isRecording={isRecording}
          arrangementCount={arrangementList.length}
          actionBarBottomPct={positions.actionBarBottom}
          arrangementsPanelContent={
            <div>
              {currentText ? (
                <div style={{
                  background: 'rgba(255,0,0,0.08)',
                  border: '1px dashed rgba(255,255,255,0.15)',
                  borderRadius: '10px',
                  padding: '8px 10px',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}>
                  <span style={{ color: 'rgba(255,80,80,0.7)', fontSize: '10px' }}>●</span>
                  <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', fontStyle: 'italic' }}>
                    {currentText}
                  </span>
                </div>
              ) : null}
              <ArrangementList items={arrangementList} mode="e2" />
            </div>
          }
        />
      </div>

    </div>
  )
}
